


















































































import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { TooltipPlacement } from '@getaccept/lib-shared/src/enums/tooltip-placement';
import { AvatarSize, IconSize, ParagraphSize } from '@getaccept/lib-ui';
import type { PropType, Ref } from '@vue/composition-api';
import { computed, defineComponent, ref, toRefs } from '@vue/composition-api';
import debounce from 'debounce';
import { useCommentMetadata } from '../../composables/comment-metadata.composable';
import { t } from '../../../helpers/translation.helper';
import type { Recipient } from '../../../types/Recipient';
import type { User } from '../../../users/types/user';
import { useReadEvents } from '../../composables/read-events.composable';
import { CommentsHelper } from '../../helpers/comments.helper';
import { CommentStatus } from '../../types/comment-status';
import type { CurrentUser } from '../../types/current-user';
import type { UpdateCommentDto } from '../../types/update-comment-dto';
import CommentInput from '../../components/CommentInput.vue';
import CommentTargetText from '../../components/CommentTargetText.vue';
import MessageContainer from '../../components/MessageContainer.vue';
import ConversationCardButtons from './ConversationCardButtons.vue';

export default defineComponent({
  components: {
    CommentInput,
    MessageContainer,
    CommentTargetText,
    ConversationCardButtons,
  },
  props: {
    comment: {
      type: Object as PropType<Comment>,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    hovered: {
      type: Boolean,
      default: false,
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    inCommentHistory: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const {
      comment,
      selected,
      hovered,
      currentUser,
      recipients,
      sender,
      readOnly,
      inCommentHistory,
    } = toRefs(props);

    const messageIdLineCountMap: Ref<{ [id: string]: number }> = ref({});

    const {
      isDraft,
      isResolved,
      isGhost,
      isResolvable,
      isCurrentUserCreator,
      richTextValue,
      visibleReplies,
    } = useCommentMetadata(comment, currentUser, readOnly, selected);

    const showTooltip = computed(() => {
      if (currentUser.value.recipientId || !selected.value) {
        return false;
      }

      return currentUser.value.userId !== (sender.value as User).id;
    });

    const showDiscardButton = computed(
      () =>
        isDraft.value &&
        !readOnly.value &&
        isCurrentUserCreator.value &&
        selected.value &&
        !comment.value.isTemporary
    );

    const repliesCanCollapse = computed(() => comment.value.replies.length > 2);

    const conversationCanCollapse = computed(() => {
      const hasLongContent = Object.values(messageIdLineCountMap.value).some(
        lineCount => lineCount > 2
      );

      return repliesCanCollapse.value || hasLongContent;
    });

    const hasContent = computed(() => CommentsHelper.hasContent(comment.value));

    const showGoToCommentButton = computed(
      () =>
        inCommentHistory.value &&
        (selected.value || hovered.value) &&
        CommentsHelper.isCommentVisible(comment.value)
    );

    const emitText = ({ richText, text }: { richText: string; text: string }) => {
      const payload: Partial<UpdateCommentDto> = { richText, text };
      emit('update', payload);
    };

    const handleResolve = () => {
      const payload: Partial<UpdateCommentDto> = { status: CommentStatus.Resolved };
      emit('update', payload);
    };

    const handleReopen = () => {
      const payload: Partial<UpdateCommentDto> = { status: CommentStatus.Active };
      emit('update', payload);
    };

    const handleSubmit = ({ richText, text }: { richText: string; text: string }) => {
      if (isDraft.value) {
        emitTextDebounce.clear();
        const payload: Partial<UpdateCommentDto> = { richText, text, status: CommentStatus.Active };
        emit('update', payload);
      } else {
        emit('create-reply', { commentId: comment.value.id, richText, text, mentions: [] });
      }
    };

    const handleMessageLineCountChanged = (id: string, lineCount: number) => {
      messageIdLineCountMap.value = {
        ...messageIdLineCountMap.value,
        [id]: lineCount,
      };
    };

    const emitTextDebounce = debounce(emitText, 300);

    const showCommentInputControls = computed(
      () => !readOnly.value && !isResolved.value && !inCommentHistory.value
    );

    const showViewEntireThreadButton = computed(
      () => conversationCanCollapse.value && !selected.value
    );

    const { mappedLatestReadEvents } = useReadEvents(
      comment,
      recipients.value,
      sender.value,
      currentUser
    );

    return {
      AvatarSize,
      TooltipPlacement,
      CommentStatus,
      IconSize,

      emitTextDebounce,
      getIsRead: CommentsHelper.isRead,
      getUserMetaData: CommentsHelper.getUserMetaData,
      t,
      handleMessageLineCountChanged,
      handleResolve,
      handleSubmit,
      handleReopen,

      isCurrentUserCreator,
      isDraft,
      isGhost,
      isResolved,
      isResolvable,

      showDiscardButton,
      richTextValue,
      visibleReplies,
      conversationCanCollapse,
      ParagraphSize,
      hasContent,
      showTooltip,
      showCommentInputControls,
      showViewEntireThreadButton,
      showGoToCommentButton,
      mappedLatestReadEvents,
    };
  },
});
